import React from 'react';
import {
  LightBulbIcon,
  PlusCircleIcon,
  CpuChipIcon,
  DocumentTextIcon,
  PhotoIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  HandThumbUpIcon,
  CheckCircleIcon,
  ArrowsPointingOutIcon,
  ShareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const scrollbarStyles = `
  .hide-scrollbar::-webkit-scrollbar { height: 4px; width: 4px; }
  .hide-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .hide-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
  .hide-scrollbar::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
`;

export default function BrainstormHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-purple-300 via-indigo-100 to-white w-full">
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <LightBulbIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Brainstorm Board</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            A collaborative visual canvas for teams to capture ideas, build strategies, and generate
            AI-powered content boards — all in real time.
          </p>
        </div>

        {/* Overview Cards */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <LightBulbIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">What You Can Do</h2>
            </div>
          </div>
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <SparklesIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">AI Board Generation</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Choose from 6 specialist AI agents — brand, marketing, copywriting, landing page,
                  SEO, or social media — to generate a fully structured board automatically.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-sky-600 rounded-xl flex items-center justify-center mb-4">
                  <UserGroupIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Collaboration</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Multiple team members can view and edit the same board simultaneously.
                  Changes sync instantly via WebSocket with presence indicators.
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-200">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Approval Workflow</h3>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Assign a Product Owner who can approve or reject individual board elements,
                  keeping the team aligned on what's accepted.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-step guide */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Step-by-Step Guide</h2>
            </div>
          </div>
          <div className="p-8 space-y-10">

            {/* Step 1 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">1</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Open a Project and Navigate to Brainstorm</h3>
                <p className="text-gray-600 mb-3 leading-relaxed">
                  Brainstorm is a module inside a project. Open any project from your dashboard,
                  then select <span className="font-semibold text-purple-700">Brainstorm</span> from the project navigation tabs.
                </p>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-800">
                  <span className="font-semibold">Tip:</span> Each project can have multiple separate brainstorm boards.
                  Use different boards for different campaigns, sprints, or departments.
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">2</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Create a New Board</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Click the <span className="font-semibold text-purple-700">+ New Board</span> button in the top toolbar. A modal will appear with two options:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <div className="flex items-center gap-3 mb-2">
                      <PlusCircleIcon className="h-6 w-6 text-indigo-600" />
                      <span className="font-semibold text-gray-900">Blank Board</span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Start with an empty canvas. Enter a board title and click <em>Create</em>.
                      You can then manually add sticky notes, documents, and pictures.
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-5 border border-purple-200">
                    <div className="flex items-center gap-3 mb-2">
                      <CpuChipIcon className="h-6 w-6 text-purple-600" />
                      <span className="font-semibold text-gray-900">AI Generated Board</span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Select an AI agent, fill in the required inputs, and the system will generate
                      a complete board with structured sticky notes and documents automatically.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 — AI Agents */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">3</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Choosing an AI Agent</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Six specialist agents are available. Each one focuses on a different business output.
                  Select the agent that matches your goal, then complete its input form.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { emoji: '🎯', name: 'Brand Identity Agent',         desc: 'Builds brand DNA, tone of voice rules, messaging pillars and positioning statement.' },
                    { emoji: '🚀', name: 'Marketing Strategy Agent',     desc: 'Creates a full marketing direction, campaign plan, and creative strategy.' },
                    { emoji: '✍️', name: 'Copywriting Agent',            desc: 'Generates ads, landing page copy, social posts, product descriptions, or blog posts.' },
                    { emoji: '🌐', name: 'Landing Page Agent',           desc: 'Produces page structure, full copy per section, CTA hierarchy, and conversion flow.' },
                    { emoji: '🔍', name: 'SEO Opportunity Agent',        desc: 'Identifies content gaps, blog and landing page ideas, and content clusters from your URL.' },
                    { emoji: '📱', name: 'Social Media Content Agent',   desc: 'Creates platform-specific post sets for LinkedIn, Instagram, and TikTok campaigns.' },
                  ].map((agent) => (
                    <div key={agent.name} className="bg-white rounded-xl p-4 border border-gray-200 flex gap-3">
                      <span className="text-2xl flex-shrink-0">{agent.emoji}</span>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{agent.name}</p>
                        <p className="text-gray-500 text-xs leading-relaxed mt-1">{agent.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-800 mt-4">
                  <span className="font-semibold">Optional inputs:</span> Most fields are optional — only fields marked
                  with an asterisk are required. You can attach reference PDFs or images to give the AI more context.
                  Providing your website URL lets the agent scan your brand for tone and content.
                </div>
              </div>
            </div>

            {/* Step 4 — AI job running */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">4</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Waiting for AI Generation</h3>
                <p className="text-gray-600 mb-3 leading-relaxed">
                  Once you click <span className="font-semibold text-purple-700">Generate Board</span>, the job is queued.
                  A progress bar appears inside the modal showing real-time status as the AI works through each step.
                </p>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2"><span className="text-purple-500 font-bold mt-0.5">•</span> The modal stays open while the AI generates content.</li>
                  <li className="flex items-start gap-2"><span className="text-purple-500 font-bold mt-0.5">•</span> When complete, the modal closes automatically and the new board loads on screen.</li>
                  <li className="flex items-start gap-2"><span className="text-purple-500 font-bold mt-0.5">•</span> If the job takes too long or fails, an error message appears with details.</li>
                </ul>
              </div>
            </div>

            {/* Step 5 — Canvas */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">5</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Using the Canvas</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  The brainstorm canvas is a large 3200 × 2000 dot-grid workspace. Elements snap to a 40px grid for clean alignment.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                    <p className="font-semibold text-gray-900 text-sm mb-1">Drag Elements</p>
                    <p className="text-yellow-800 text-xs leading-relaxed">Click and drag any sticky note, document, or image to reposition it anywhere on the canvas.</p>
                  </div>
                  <div className="bg-sky-50 rounded-xl p-4 border border-sky-200">
                    <p className="font-semibold text-gray-900 text-sm mb-1">Pan the Canvas</p>
                    <p className="text-sky-800 text-xs leading-relaxed">Use the scroll bars or drag the canvas background to navigate around the workspace.</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="font-semibold text-gray-900 text-sm mb-1">Auto-Save</p>
                    <p className="text-green-800 text-xs leading-relaxed">Changes are saved automatically after a short pause. The save indicator in the toolbar shows current save state.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 6 — Adding elements */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">6</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Adding Elements Manually</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Use the toolbar buttons to add three types of elements to the canvas:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🗒️</span>
                      <span className="font-semibold text-gray-900">Sticky Note</span>
                    </div>
                    <ol className="text-yellow-800 text-xs space-y-1 leading-relaxed list-decimal ml-4">
                      <li>Click <em>Add Note</em> in the toolbar</li>
                      <li>A grid picker appears — select the cell position</li>
                      <li>A note is placed at that grid position</li>
                      <li>Click the note to open the detail editor and add your text</li>
                      <li>Pick a colour from the six preset options</li>
                    </ol>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-gray-900">Document</span>
                    </div>
                    <ol className="text-blue-800 text-xs space-y-1 leading-relaxed list-decimal ml-4">
                      <li>Click <em>Add Doc</em> in the toolbar</li>
                      <li>Select a grid position</li>
                      <li>Click the document card to open the doc editor</li>
                      <li>Type or paste your content in rich text format</li>
                      <li>Use <em>AI Assist</em> to have the AI expand or rewrite content</li>
                    </ol>
                  </div>
                  <div className="bg-pink-50 rounded-xl p-5 border border-pink-200">
                    <div className="flex items-center gap-2 mb-2">
                      <PhotoIcon className="h-5 w-5 text-pink-600" />
                      <span className="font-semibold text-gray-900">Picture</span>
                    </div>
                    <ol className="text-pink-800 text-xs space-y-1 leading-relaxed list-decimal ml-4">
                      <li>Click <em>Add Picture</em> in the toolbar</li>
                      <li>Select a grid position</li>
                      <li>In the picture modal: <em>Generate</em> an image with AI or <em>Upload</em> your own</li>
                      <li>For AI generation, enter a prompt and click Generate</li>
                      <li>Accept or discard the result, then save</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 7 — Collaboration */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">7</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Inviting Team Members</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  By default only the board creator has access. To allow others to view and edit:
                </p>
                <ol className="space-y-2 text-gray-600 text-sm leading-relaxed list-decimal ml-5">
                  <li>Click the <span className="font-semibold text-purple-700">Invite</span> button in the toolbar.</li>
                  <li>A panel lists all staff in your organisation. Tick the staff members you want to add.</li>
                  <li>Optionally copy the board's share link to send directly to someone.</li>
                  <li>Click <span className="font-semibold">Done</span> — invited members can now open this board from their project view.</li>
                </ol>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-800 mt-4">
                  <span className="font-semibold">Presence indicators:</span> When team members are active on the same board,
                  their avatars appear in the toolbar so you can see who is online.
                </div>
              </div>
            </div>

            {/* Step 8 — Product Owner */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">8</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Setting a Product Owner</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  The Product Owner role unlocks the approval workflow. Only the Product Owner can approve
                  or unapprove individual board elements.
                </p>
                <ol className="space-y-2 text-gray-600 text-sm leading-relaxed list-decimal ml-5">
                  <li>In the toolbar, click the <span className="font-semibold text-purple-700">Set PO</span> button (visible to board creators).</li>
                  <li>Select a staff member from the invited list.</li>
                  <li>Their name now shows as <em>PO: [Name]</em> in the toolbar.</li>
                  <li>The Product Owner sees an <span className="font-semibold">Approve</span> button on every element — approved items show a ✅ badge.</li>
                </ol>
              </div>
            </div>

            {/* Step 9 — Engagement */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">9</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Likes, Comments & Polls</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Every element supports engagement features to gather team feedback without leaving the board.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <div className="flex items-center gap-2 mb-2">
                      <HandThumbUpIcon className="h-5 w-5 text-red-500" />
                      <span className="font-semibold text-gray-900 text-sm">Likes</span>
                    </div>
                    <p className="text-red-800 text-xs leading-relaxed">Click the like icon on any element to upvote it. The count updates in real time for all viewers.</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-500" />
                      <span className="font-semibold text-gray-900 text-sm">Comments</span>
                    </div>
                    <p className="text-blue-800 text-xs leading-relaxed">Click the comment icon to open the engagement modal. Add a comment, reply to others, or delete your own comments.</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowsPointingOutIcon className="h-5 w-5 text-green-500" />
                      <span className="font-semibold text-gray-900 text-sm">Polls</span>
                    </div>
                    <p className="text-green-800 text-xs leading-relaxed">Vote Yes or No on any element to create a quick team poll. Results are shown as live vote counts.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 10 — Real-time sync */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">10</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Sync</h3>
                <p className="text-gray-600 mb-3 leading-relaxed">
                  The board uses a live WebSocket connection to sync changes between all active participants instantly.
                </p>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2"><span className="text-purple-500 font-bold mt-0.5">•</span> A coloured dot in the toolbar shows your connection status (green = live, amber = reconnecting).</li>
                  <li className="flex items-start gap-2"><span className="text-purple-500 font-bold mt-0.5">•</span> Any element you move, edit, add, or delete is instantly broadcast to all other viewers.</li>
                  <li className="flex items-start gap-2"><span className="text-purple-500 font-bold mt-0.5">•</span> If the connection is lost, the board falls back to periodic auto-save mode and reconnects automatically.</li>
                </ul>
              </div>
            </div>

            {/* Step 11 — Multiple boards */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">11</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Switching Between Boards</h3>
                <p className="text-gray-600 mb-3 leading-relaxed">
                  A project can contain many brainstorm boards. Use the board selector dropdown in the
                  top-left of the Brainstorm view to switch between boards without leaving the project.
                </p>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-800">
                  <span className="font-semibold">Deleting a board:</span> Open the board you want to remove, then use the
                  <em> Delete Board</em> option in the toolbar. This action is permanent and cannot be undone.
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <ShareIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Quick Reference — Toolbar Buttons</h2>
            </div>
          </div>
          <div className="p-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Button</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">What it does</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Who can use it</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['+ New Board',    'Creates a blank or AI-generated board',                     'All invited members'],
                    ['Add Note',       'Adds a sticky note to the canvas via grid picker',          'All invited members'],
                    ['Add Doc',        'Adds a document card to the canvas',                        'All invited members'],
                    ['Add Picture',    'Adds an image card (upload or AI generate)',                'All invited members'],
                    ['Invite',         'Opens the staff invite panel',                              'Board creator'],
                    ['Set PO',         'Assigns a Product Owner to manage approvals',               'Board creator'],
                    ['Delete Board',   'Permanently deletes the current board',                     'Board creator'],
                    ['Approve (✅)',    'Marks an element as approved',                              'Product Owner only'],
                    ['Like / Comment', 'Opens the engagement panel for the element',               'All invited members'],
                  ].map(([btn, desc, who]) => (
                    <tr key={btn} className="hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-purple-700">{btn}</td>
                      <td className="py-3 px-4 text-gray-600">{desc}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{who}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-3xl shadow-2xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-6">Tips for Getting the Best Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Provide your website URL', body: 'When using any AI agent, entering your website URL lets the AI scan your existing brand and tailor the output to your tone and style.' },
              { title: 'Upload reference documents', body: 'Attach brand guidelines, briefs, or existing strategy docs as PDFs. The AI will read them and incorporate them into the generated board.' },
              { title: 'Use numbered sticky notes', body: 'If you assign a number to sticky notes, the canvas automatically draws flow-arrows between them in numerical order to show a process flow.' },
              { title: 'Combine AI + manual editing', body: 'Generate a board with an AI agent to get a structured starting point, then manually edit elements, change colours, and reorganise to suit your needs.' },
            ].map((tip) => (
              <div key={tip.title} className="bg-white/10 rounded-2xl p-5">
                <p className="font-semibold text-white mb-1">{tip.title}</p>
                <p className="text-purple-100 text-sm leading-relaxed">{tip.body}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
